import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  metaDesc: string;
  h1: string;
  author: string;
  publishedDate: string;
  updatedDate: string;
  children: React.ReactNode;
  breadcrumbs: { label: string; href: string }[];
}

export default function ArticleLayout({
  title,
  metaDesc,
  h1,
  author,
  publishedDate,
  updatedDate,
  children,
  breadcrumbs,
}: ArticleLayoutProps) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', metaDesc);
  }, [title, metaDesc]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Breadcrumbs */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-4 bg-background-100 border-b border-background-200/70">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line text-foreground-400" />}
                <a href={crumb.href} className="hover:text-primary-600 transition-colors">
                  {crumb.label}
                </a>
              </span>
            ))}
          </nav>
        </div>

        {/* Article Header */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-6">
              {h1}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                Published: {publishedDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                Updated: {updatedDate}
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto article-content">
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}